import React, { useEffect, useRef, useState } from "react";
import { Button, Field } from "@fluentui/react-components";
import { useTaskPane } from "../../provider/TaskPaneProvider";
import services from "../../services/indexService";

const SearchCriteria = ({ updateState, clearToaster, actionType, authToken, authStatus, profileStatus, locationStatus, onRetryBootstrap }) => {
  const { locationData = [], taskPaneState, filterTypeByCriteriaType, setPoliciesDetailsForInsured,
    setTaskPaneState, setSelectedGridItem, getLocationSpecificInfo, locationsLoading = false,
    criteriaLoading = false, locationError, retryLocations } = useTaskPane();
  const authReady = !!(authToken || localStorage.getItem("auth-token"));
  const hosted = false;
  const [pending, setPending] = useState(null);
  const [feedback, setFeedback] = useState("");
  const request = useRef(0);
  const busy = useRef(false);
  const contextKey = JSON.stringify([taskPaneState.compId, taskPaneState.comLocId, taskPaneState.criteria, taskPaneState.userId]);
  const context = useRef(contextKey);
  context.current = contextKey;
  useEffect(() => {
    request.current += 1;
    busy.current = false;
    setPending(null);
    setFeedback("");
    return () => { request.current += 1; busy.current = false; };
  }, [contextKey]);
  const ready = authReady && !!taskPaneState.comLocId && !!taskPaneState.compId && !!taskPaneState.criteria
    && !locationsLoading && !criteriaLoading && authStatus !== "error" && profileStatus !== "error" && locationStatus !== "error";
  const searchReady = ready && !!taskPaneState.searchText?.trim();
  const recentReady = ready && filterTypeByCriteriaType === "Type Insured/Policy";
  const begin = (action) => {
    busy.current = true;
    const id = ++request.current;
    const key = context.current;
    setPending(action);
    setFeedback("");
    clearToaster();
    setSelectedGridItem(new Set());
    setPoliciesDetailsForInsured([]);
    return { current: () => id === request.current && key === context.current };
  };
  const finish = (operation) => {
    if (!operation.current()) return;
    busy.current = false;
    setPending(null);
  };
  const handleSearch = async () => {
    if (busy.current || !searchReady) return;
    const operation = begin("search");
    try {
      const result = await services.searchByCriteria(taskPaneState.compId, taskPaneState.comLocId,
        taskPaneState.criteria, taskPaneState.searchText, taskPaneState.userId);
      if (!operation.current()) return;
      if (filterTypeByCriteriaType === "Type Insured/Policy") {
        const policies = (result?.srchPolicyDeatilsResult || []).map(row => ({ ...row, policyID: row.sub }));
        const insured = result?.srchInsuredByPolicyNumberResult?.length
          ? result.srchInsuredByPolicyNumberResult : result?.srchInsuredByNameResult || [];
        const selectedId = result?.policyId || policies[0]?.policyID;
        setPoliciesDetailsForInsured(policies);
        setSelectedGridItem(selectedId ? new Set([selectedId]) : new Set());
        setTaskPaneState(prev => ({ ...prev, insuredForRecentPoliciesResults: insured,
          insuredId: result?.insuredId || insured[0]?.insuredID || "", isSearch: !!(insured.length || policies.length) }));
        if (!insured.length && !policies.length) setFeedback("No matches found. Update your query and try again.");
      } else if (filterTypeByCriteriaType === "Type Producer") {
        const policies = (result?.srchProducerResult || []).map(row => ({ ...row, policyID: row.producerId }));
        const selectedId = result?.producerId || policies[0]?.policyID;
        setPoliciesDetailsForInsured(policies);
        setSelectedGridItem(selectedId ? new Set([selectedId]) : new Set());
        if (!policies.length) setFeedback("No matches found. Update your query and try again.");
      }
    } catch (_) {
      if (operation.current()) setFeedback("Search could not be completed. Your query is unchanged. Try Search again.");
    } finally { finish(operation); }
  };
  const handleGetRecent = async () => {
    if (busy.current || !recentReady) return;
    const operation = begin("recent");
    try {
      setTaskPaneState(prev => ({ ...prev, searchText: "" }));
      await getLocationSpecificInfo(taskPaneState.comLocId, taskPaneState.compId);
    } catch (_) {
      if (operation.current()) setFeedback("Recent items could not be loaded. Try Get Recent again.");
    } finally { finish(operation); }
  };
  const failedStage = authStatus === "error" ? "auth" : profileStatus === "error" ? "profile" : locationStatus === "error" ? "location" : null;
  const statusText = failedStage === "auth" ? "Sign-in failed. Please try again."
    : failedStage === "profile" ? "Profile data could not be loaded."
    : failedStage === "location" || locationError ? "Locations could not be loaded."
    : locationsLoading || (!authReady && hosted) ? "Loading locations..."
    : authStatus === "loading" ? "Signing in..."
    : profileStatus === "loading" ? "Loading profile..."
    : locationStatus === "loading" ? "Loading locations..."
    : locationStatus === "empty" || profileStatus === "empty" ? "No locations are available for your account." : "";
  return <>
    {actionType === "sendMailAndOpenTaskPane" && <p className="text-sm bg-yellow-100 text-yellow-800">If you need to edit your email draft, close this window before making changes.</p>}
    {statusText && <div role="status" aria-live="polite" className="text-sm text-gray-600 mb-2">
      {statusText}
      {failedStage && onRetryBootstrap && <Button onClick={() => onRetryBootstrap(failedStage)}>Retry</Button>}
      {!failedStage && locationError && retryLocations && <Button onClick={retryLocations}>Retry locations</Button>}
    </div>}
    <div className="gap-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2">
      <div className="items-center gap-2 w-full flex flex-row seltest">
        <label htmlFor="location-select" className="h3-text text-left block text-gray-600 text-xs sm:text-sm lg:text-base font-medium mb-1 w-[4rem]">Location</label>
        <select id="location-select" value={taskPaneState.comLocId || ""} onChange={e => updateState("comLocId", e.target.value)}
          disabled={locationsLoading || !locationData.length} aria-busy={locationsLoading}
          className="mr-12 sm:mr-0 decorated input-layout rounded py-0 px-0 text-xs sm:text-sm lg:text-base text-gray-700 w-full">
          {locationData.length !== 1 && <option value="">{locationsLoading || (!authReady && hosted) ? "Loading locations..." : "Select Location"}</option>}
          {locationData.map(location => <option key={location.compLocID} value={location.compLocID}>{location.companyLocation}</option>)}
        </select>
      </div>
      <div className="absolute right-5">{process.env.AppVersion}</div>
    </div>
    <h2 className="h2-text text-left text-base font-semibold text-black-700 mb-1">Insured / Policy Information</h2>
    <h2 className="h3-text text-left text-base font-semibold text-gray-700 mb-1">Search Criteria</h2>
    <div className="gap-2 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3">
      <Field className="items-center gap-2 w-full flex flex-column">
        <label htmlFor="criteria-select" className="sr-only">Search criteria</label>
        <select id="criteria-select" value={taskPaneState.criteria || ""} onChange={e => updateState("criteria", e.target.value)}
          disabled={criteriaLoading || !taskPaneState.criteriaItems?.length} aria-busy={criteriaLoading}
          className="input-layout rounded py-0 px-0 text-xs sm:text-sm lg:text-base text-gray-700 w-full">
          {!taskPaneState.criteriaItems?.length && <option value="">{criteriaLoading ? "Loading criteria..." : "Select location first"}</option>}
          {taskPaneState.criteriaItems?.map(item => <option key={item.criteriaName} value={item.criteriaName}>{item.criteriaName}</option>)}
        </select>
      </Field>
      <Field className="items-center gap-2 w-full flex flex-column">
        <label htmlFor="search-text" className="sr-only">Search term</label>
        <input id="search-text" value={taskPaneState.searchText || ""} onChange={e => updateState("searchText", e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSearch(); } }} placeholder="Enter search term"
          className="input-layout rounded py-1 px-2 text-xs sm:text-sm lg:text-base text-gray-700 w-full" />
      </Field>
      <div className="gap-2 w-full flex flex-row items-end justify-flex-start">
        <Button onClick={handleSearch} appearance="primary" disabled={!searchReady || !!pending} aria-busy={pending === "search"}
          className={!searchReady || pending ? "btn-com-disable" : "btn-com"}>{pending === "search" ? "Searching..." : "Search"}</Button>
        <Button onClick={handleGetRecent} appearance="primary" disabled={!recentReady || !!pending} aria-busy={pending === "recent"}
          className={!recentReady || pending ? "btn-com-disable" : "btn-com"}>{pending === "recent" ? "Loading..." : "Get Recent"}</Button>
      </div>
    </div>
    {feedback && <p role="status" aria-live="polite" className="text-sm mt-2">{feedback}</p>}
  </>;
};
export default SearchCriteria;
